import { CommonModule, LowerCasePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DoBootstrap, Injector, NgModule, inject } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApxButtonModule, PorFormsModule, ApxInputModule } from '@por/shared/ui';
import { Language } from '@por/shared/core';
import { UIMissingTranslationHandler, UITranslateLoader } from '@por/shared/ui/translations';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as EnGBTranslations from '../assets/en-GB.json';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as EnUSTranslations from '../assets/en-US.json';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as EnAUTranslations from '../assets/en-AU.json';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as EsESTranslations from '../assets/es-ES.json';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as FrCATranslations from '../assets/fr-CA.json';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as FrFRTranslations from '../assets/fr-FR.json';
import { BusyIndicatorComponentComponent } from './busy-indicator-component/busy-indicator-component.component';
import { ChangePasswordComponentComponent } from './change-password-component/change-password-component.component';
import { ConsumerLoginComponent } from './consumer-login.component/consumer-login.component';
import { ForgotPasswordComponentComponent } from './forgot-password-component/forgot-password-component.component';
import { ConsumerLoginApiService } from './services/consumer-login-api.service';
import { AlertComponentComponent } from './shared/alert-component/alert-component.component';
import { SvgIconComponent } from './shared/icon-component/icon-component.component';
import { LoaderComponentComponent } from './shared/loader-component/loader-component.component';
import { SigninComponentComponent } from './signin-component/signin-component.component';
import { SignupComponentComponent } from './signup-component/signup-component.component';
import { VerifyComponentComponent } from './verify-component/verify-component.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConsumerPortalDemoModule } from '@por/consumer-portal-demo';
import { AddFeatureClassDirective } from './shared/directive/add-feature-class.directive';

import { EcomSharedModule } from '@por/ecom-shared';
import { PorIconCheck, PorIconEmail, PorIconLock, PorIconMagnifier, PorIconSpinner } from '@por/icon-lib/icons';
import { PorIconModule } from '@por/shared/ui/icon-lib';
import { CustomerRedirectionComponent } from './consumer-redirection/customer-selection.component';
import { PrivacyTermsComponent } from './privacy-terms-component/privacy-terms-component.component';
import { AccountService } from './services/accounts.service';
import { AuthService } from './services/auth.service';
import { PrivacyPolicyService } from './services/privacy-policy.service';
import { SiteSettingService } from './services/site-setting.service';
import { SmsService } from './services/sms.service';
import { VerificationCodeHelpTextComponent } from './verification-code-help-text/verification-code-help-text.component';
import { SmsVerificationComponent } from './sms-verification/sms-verification.component';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ApxButtonModule,
        PorFormsModule,
        ApxInputModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: () => {
                    return new UITranslateLoader({
                        [Language.EN_US]: EnUSTranslations,
                        [Language.EN_GB]: EnGBTranslations,
                        [Language.EN_AU]: EnAUTranslations,
                        [Language.ES_ES]: EsESTranslations,
                        [Language.FR_CA]: FrCATranslations,
                        [Language.FR_FR]: FrFRTranslations
                    });
                }
            },
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: UIMissingTranslationHandler },
            defaultLanguage: Language.EN_US
        }),
        ConsumerPortalDemoModule,
        EcomSharedModule,
        PorIconModule.forRoot([PorIconCheck, PorIconEmail, PorIconLock, PorIconMagnifier, PorIconSpinner])
    ],
    declarations: [
        ConsumerLoginComponent,
        SignupComponentComponent,
        SigninComponentComponent,
        PrivacyTermsComponent,
        VerifyComponentComponent,
        AlertComponentComponent,
        LoaderComponentComponent,
        SvgIconComponent,
        ForgotPasswordComponentComponent,
        ChangePasswordComponentComponent,
        BusyIndicatorComponentComponent,
        AddFeatureClassDirective,
        CustomerRedirectionComponent,
        VerificationCodeHelpTextComponent,
        SmsVerificationComponent
    ],
    providers: [
        ConsumerLoginApiService,
        SiteSettingService,
        TranslateService,
        LowerCasePipe,
        AccountService,
        AuthService,
        PrivacyPolicyService,
        provideHttpClient(withInterceptorsFromDi()),
        SmsService
    ],
    exports: [ConsumerLoginComponent, CustomerRedirectionComponent]
})
export class ConsumerLoginModule implements DoBootstrap {
    private readonly injector = inject(Injector);

    ngDoBootstrap(): void {
        const signin = createCustomElement(SigninComponentComponent, {
            injector: this.injector
        });
        customElements.define('por-signin', signin);

        const signup = createCustomElement(SignupComponentComponent, {
            injector: this.injector
        });
        customElements.define('por-signup', signup);
        const verify = createCustomElement(VerifyComponentComponent, {
            injector: this.injector
        });
        customElements.define('por-verify', verify);
    }
}
